import ErrorOutlineIcon from "@mui/icons-material/ErrorOutlined";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { PermissionGuard } from "auth/PermissionGuard";
import { RoleGuard } from "auth/RoleGuard";
import { AppLayout } from "components/Layout/AppLayout";
import { MaterialTypeGate } from "components/routing/MaterialTypeGate";
import { type ComponentType, lazy } from "react";
import { createBrowserRouter, Navigate } from "react-router";

function lazyWithRetry<T extends ComponentType>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(() =>
    factory().catch((error: unknown) => {
      const key = "chunk_reload";
      const hasReloaded = sessionStorage.getItem(key);
      if (!hasReloaded) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        return new Promise<{ default: T }>(() => {});
      }
      sessionStorage.removeItem(key);
      throw error;
    }),
  );
}

const DashboardPage = lazyWithRetry(() =>
  import("pages/Dashboard/DashboardPage").then((m) => ({
    default: m.DashboardPage,
  })),
);
const CreateCasePage = lazyWithRetry(() =>
  import("pages/CreateCase/CreateCasePage").then((m) => ({
    default: m.CreateCasePage,
  })),
);
const CaseDetailPage = lazyWithRetry(() =>
  import("pages/CaseDetail/CaseDetailPage").then((m) => ({
    default: m.CaseDetailPage,
  })),
);
const KanbanPage = lazyWithRetry(() =>
  import("pages/Kanban/KanbanPage").then((m) => ({
    default: m.KanbanPage,
  })),
);
const ReportsPage = lazyWithRetry(() =>
  import("pages/Reports/ReportsPage").then((m) => ({
    default: m.ReportsPage,
  })),
);
const AdminRolesPage = lazyWithRetry(() =>
  import("pages/Admin/AdminRolesPage").then((m) => ({
    default: m.AdminRolesPage,
  })),
);
const MaterialListPage = lazyWithRetry(() =>
  import("pages/MaterialList/MaterialListPage").then((m) => ({
    default: m.MaterialListPage,
  })),
);
const MaterialKanbanPage = lazyWithRetry(() =>
  import("pages/MaterialKanban/MaterialKanbanPage").then((m) => ({
    default: m.MaterialKanbanPage,
  })),
);
const CreateMavWarehousePage = lazyWithRetry(() =>
  import("pages/CreateMaterialCase/CreateMavPage").then((m) => {
    const WarehousePage = () => (
      <m.CreateMavPage variant="WarehousePurchasing" />
    );
    WarehousePage.displayName = "CreateMavWarehousePage";
    return { default: WarehousePage };
  }),
);
const CreateMavProductionPage = lazyWithRetry(() =>
  import("pages/CreateMaterialCase/CreateMavPage").then((m) => {
    const ProductionPage = () => <m.CreateMavPage variant="Production" />;
    ProductionPage.displayName = "CreateMavProductionPage";
    return { default: ProductionPage };
  }),
);
const CreateMstPage = lazyWithRetry(() =>
  import("pages/CreateMaterialCase/CreateMstPage").then((m) => ({
    default: m.CreateMstPage,
  })),
);
const MaterialDetailPage = lazyWithRetry(() =>
  import("pages/MaterialDetail/MaterialDetailPage").then((m) => ({
    default: m.MaterialDetailPage,
  })),
);
const MyTasksPage = lazyWithRetry(() =>
  import("pages/MyTasks/MyTasksPage").then((m) => ({
    default: m.MyTasksPage,
  })),
);

export const router = createBrowserRouter([
  {
    path: "/",
    element: <AppLayout />,
    errorElement: (
      <Grid
        container
        sx={{
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, mb: 2, color: "error.main" }} />
          <Typography variant="h5">Något gick fel</Typography>
        </Box>
      </Grid>
    ),
    children: [
      {
        index: true,
        element: (
          <PermissionGuard>
            <MyTasksPage />
          </PermissionGuard>
        ),
      },
      {
        path: "mbo/overview",
        element: (
          <PermissionGuard>
            <DashboardPage />
          </PermissionGuard>
        ),
      },
      {
        path: "cases/new",
        element: (
          <PermissionGuard>
            <CreateCasePage />
          </PermissionGuard>
        ),
      },
      {
        path: "cases/:id",
        element: (
          <PermissionGuard>
            <CaseDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: "kanban",
        element: (
          <PermissionGuard>
            <KanbanPage />
          </PermissionGuard>
        ),
      },
      {
        path: "reports",
        element: (
          <RoleGuard roles={["Chef", "Admin"]}>
            <ReportsPage />
          </RoleGuard>
        ),
      },
      {
        path: "admin",
        element: <Navigate to="/admin/roles" replace />,
      },
      {
        path: "admin/roles",
        element: (
          <RoleGuard roles={["Admin"]}>
            <AdminRolesPage />
          </RoleGuard>
        ),
      },
      {
        path: "material/kanban",
        element: <Navigate to="/material/kanban/mav" replace />,
      },
      {
        path: "material/kanban/:typeFilter",
        element: (
          <MaterialTypeGate fallback="/material/kanban/mav">
            <PermissionGuard>
              <MaterialKanbanPage />
            </PermissionGuard>
          </MaterialTypeGate>
        ),
      },
      {
        path: "material/my-tasks",
        element: (
          <PermissionGuard>
            <MyTasksPage />
          </PermissionGuard>
        ),
      },
      {
        path: "material/:typeFilter",
        element: (
          <MaterialTypeGate fallback="/material/mav">
            <PermissionGuard>
              <MaterialListPage />
            </PermissionGuard>
          </MaterialTypeGate>
        ),
      },
      {
        path: "material/:caseType/:id",
        element: (
          <PermissionGuard>
            <MaterialDetailPage />
          </PermissionGuard>
        ),
      },
      {
        path: "material/new/mav",
        element: <Navigate to="/material/mav" replace />,
      },
      {
        path: "material/new/mav/lager",
        element: (
          <PermissionGuard>
            <CreateMavWarehousePage />
          </PermissionGuard>
        ),
      },
      {
        path: "material/new/mav/produktion",
        element: (
          <PermissionGuard>
            <CreateMavProductionPage />
          </PermissionGuard>
        ),
      },
      {
        path: "material/new/mst",
        element: (
          <PermissionGuard>
            <CreateMstPage />
          </PermissionGuard>
        ),
      },
      {
        path: "*",
        element: (
          <Grid
            container
            sx={{
              height: "100vh",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <ErrorOutlineIcon
                sx={{ fontSize: 64, mb: 2, color: "text.secondary" }}
              />
              <Typography variant="h5">Sidan hittades inte</Typography>
            </Box>
          </Grid>
        ),
      },
    ],
  },
]);
